import type { NombreIcono } from '../componentes/iconos';

/**
 * Textos de «Firma en el centro» (9.13), tal cual el diseño (`diseno/citas/9.13-firma-en-el-centro.html`). La hora del envío y
 * los reenvíos de hoy los da la API (`SignatureRequest.sent_at` y `resends_today`, G873); el panel no los cuenta.
 */
export const T_FIRMA = {
    eyebrowTablet: (negocio: string) => `${negocio} · Tablet de recepción`,
    eyebrowPanel: (negocio: string) => `${negocio} · Desde el panel`,
    titulo: 'Firmas pendientes de hoy',
    tituloSuyo: 'Tus firmas pendientes de hoy',
    tituloCambiar: 'Cambiar de día',
    panel: 'Panel',
    bloquear: 'Bloquear',
    ayuda: 'Cómo funciona la firma en el centro',
    porFirmar: (n: number) => `Por firmar · ${String(n)}`,
    firmadosHoy: 'Ya firmados hoy',
    firmasDeHoy: 'Firmas de hoy',
    // Estados de toda la pantalla
    errorTitulo: 'No he podido cargar las firmas de hoy',
    errorTexto: 'Puede ser la conexión. Prueba de nuevo.',
    reintentar: 'Reintentar',
    vaciaTitulo: 'Hoy no hay firmas pendientes',
    vaciaTexto: 'Las clientas de hoy ya han firmado lo que hacía falta, o sus servicios no piden consentimiento.',
    vaciaTextoSuyo: 'Tus clientas de hoy ya han firmado lo que hacía falta, o sus servicios no piden consentimiento.',
    noContratadoTitulo: 'Firma eIDAS no está contratada',
    noContratadoTexto: 'Pide a tus clientas que firmen consentimientos con validez legal, desde el móvil o aquí en la tablet. Pídeselo al propietario.',
    noContratadoIncluye: ['25 firmas al mes incluidas', 'Se piden solas al confirmar la cita', 'Guardadas el tiempo que obliga la ley'],
    sinPermisoTitulo: 'No tienes permiso para recoger firmas',
    sinPermisoTexto: (negocio: string) => `Tu rol en ${negocio} no puede recoger firmas en el centro. Pídeselo al propietario o a un administrador.`,
    // Ficha
    chipFirmado: 'Firmado',
    chipEnlace: 'Enlace enviado',
    chipPendiente: 'Firma pendiente',
    hoyDe: (desde: string, hasta: string, pro: string) => `Hoy, de ${desde} a ${hasta}${pro === '' ? '' : ` · con ${pro}`}`,
    firmadoA: (hora: string) => `Firmado a las ${hora}`,
    firmadoTexto: 'El documento firmado ya está en su cita y en «Mis documentos» de la clienta.',
    enlaceTitulo: 'Le mandamos el enlace y aún no ha firmado',
    enlaceTexto: (enviado: string, hasta: string) => `Enviado a las ${enviado}. Puede firmar desde su móvil hasta las ${hasta}; si no, aquí al llegar.`,
    rechazoTitulo: 'No quiso firmar desde el móvil',
    rechazoTexto: (enviado: string) => `Le mandamos el enlace a las ${enviado} y lo rechazó. Habladlo al llegar: puede firmar aquí o cambiar la cita.`,
    plazoTitulo: 'No firmó a tiempo desde el móvil',
    plazoTexto: (enviado: string, recordado: string, hasta: string) =>
        `Le mandamos el enlace a las ${enviado}${recordado === '' ? '' : ` y un recordatorio a las ${recordado}`}. El plazo terminó a las ${hasta}.`,
    noSeCancela: ' La cita no se cancela por no firmar.',
    cargandoDocumento: 'Cargando el documento',
    firmarAqui: 'Firmar en esta tablet',
    preparando: 'Preparando la firma…',
    reenviar: 'Reenviar el enlace',
    cambiarDia: 'Cambiar de día',
    proveedorCaido: 'El servicio de firma no responde. No se ha abierto nada; prueba otra vez en unos minutos.',
    yaFirmoTitulo: (nombre: string) => `${nombre} acaba de firmar desde su móvil`,
    yaFirmoTexto: 'No hace falta firmar aquí. El documento ya está en su cita.',
    haFirmadoTitulo: (nombre: string) => `${nombre} ha firmado`,
    haFirmadoTexto: 'El documento firmado ya está en su cita y en sus documentos.',
    noFirmadoTitulo: 'No se ha firmado',
    noFirmadoTexto: (nombre: string) => `${nombre} ha devuelto la tablet sin firmar. Puedes intentarlo otra vez o cambiar la cita.`,
    // Entregar y modo cliente
    entregaTitulo: (nombre: string) => `Dale la tablet a ${nombre}`,
    entregaTexto: 'Solo verá su documento y dónde firmar: nada del panel. Para volver a tu sesión te pediremos tu PIN.',
    empezar: 'Ya la tiene: empezar',
    cancelar: 'Cancelar',
    firmaDeUnDocumento: 'Firma de un documento',
    devolver: 'Devolver la tablet',
    devolverA: (nombre: string) => `Devolver la tablet a ${nombre}`,
    guardandoTitulo: 'Guardando tu firma…',
    guardandoTexto: 'Un momento: el servicio de firma lo está guardando.',
    graciasTitulo: (nombre: string) => `Gracias, ${nombre}. Ya está firmado`,
    graciasTexto: (yo: string) => `Te mandamos una copia. Ahora devuelve la tablet a ${yo}.`,
    // Propuesto (sin dibujo): la vuelta del proveedor sin firma terminada.
    sinTerminarTitulo: 'La firma no se ha terminado',
    sinTerminarTexto: (yo: string) => `Devuelve la tablet a ${yo}.`,
    // PIN para volver
    hola: (nombre: string) => `Hola, ${nombre}`,
    escribePin: 'Escribe tu PIN para volver a tu sesión',
    notaFirmada: 'La firma está guardada. Solo tú puedes volver a tu sesión.',
    notaSinFirmar: 'La firma no se ha terminado. Solo tú puedes volver a tu sesión.',
    // G915: la API no cuenta los intentos (`attempts_remaining` nulo): sin cifra.
    pinMaloSinCifra: 'PIN incorrecto.',
    pinMalo: (quedan: number) => (quedan === 1 ? 'PIN incorrecto. Queda 1 intento.' : `PIN incorrecto. Quedan ${String(quedan)} intentos.`),
    // Cambiar de día
    volverFirma: 'Volver a la firma',
    cambiarTitulo: (nombre: string) => `Cambiar la cita de ${nombre}`,
    cambiarSub: (hora: string, servicio: string, pro: string) => `Ahora: hoy a las ${hora}, ${servicio.toLowerCase()}${pro === '' ? '' : ` con ${pro}`}. Elige otro día y hora.`,
    conQuien: 'Con quién',
    con: (pro: string) => `Con ${pro}`,
    cualquiera: 'Cualquiera',
    dia: 'Día',
    horasLibres: 'Horas libres',
    sinHueco: 'No queda hueco ese día.',
    cerrado: 'Ese día está cerrado.',
    otroDia: 'Prueba con otro día.',
    notaCambiar: 'Al elegir la hora te enseñamos lo que supone antes de cambiarla. A la clienta le avisamos y le volvemos a pedir la firma para la nueva fecha.',
    yaNoLibre: 'Esa hora ya no está libre. Elige otra.',
    cambiarPregunta: (nombre: string, dia: string, hora: string) => `¿Pasar la cita de ${nombre} al ${dia}, ${hora}?`,
    cambiarTexto: (pro: string | null) => `Le avisamos por WhatsApp y le volvemos a pedir la firma para la nueva fecha. ${pro === null ? 'La hará quien esté libre.' : `Sigue con ${pro}.`}`,
    ahora: 'Ahora',
    nueva: 'Nueva',
    importe: 'Importe',
    sinCambios: 'Sin cambios',
    motivo: 'Motivo (opcional)',
    motivoAyuda: 'Queda en el historial de la cita.',
    cambiarAccion: 'Cambiar y avisar',
    cambiando: 'Cambiando…',
    cambiadaTitulo: (nombre: string) => `Cita de ${nombre} cambiada`,
    cambiadaTexto: (dia: string, hora: string) => `Ahora es el ${dia} a las ${hora}. Le hemos avisado.`,
    // Reenviar
    reenviarPregunta: (nombre: string) => `¿Reenviar el enlace a ${nombre}?`,
    reenviarTexto: (veces: number) =>
        `Le llega por WhatsApp, al número que ya tiene; si no, por email. Se puede reenviar 3 veces al día: ${veces > 0 ? `ya lo has hecho ${String(veces)} ${veces === 1 ? 'vez' : 'veces'}` : 'aún no lo has hecho'}.`,
    reenviarAccion: 'Reenviar',
    reenviando: 'Reenviando…',
    reenviadoTitulo: 'Enlace reenviado',
    reenviadoTexto: (nombre: string, hasta: string) => `${nombre} puede firmar desde su móvil hasta las ${hasta}.`,
    // Tablet bloqueada (vuelve a «¿Quién eres?» de 1.13)
    bloqueadaTitulo: 'Tablet bloqueada',
    bloqueadaTexto: 'Para seguir, toca tu nombre y escribe tu PIN.',
    quienEres: '¿Quién eres?',
} as const;

export const AYUDA_FIRMA: readonly (readonly [NombreIcono, string, string])[] = [
    ['list-checks', 'Qué sale aquí', 'Las citas de hoy cuyo consentimiento sigue sin firmar: porque no lo firmaron desde el móvil, lo rechazaron o venció el plazo.'],
    ['tablet-smartphone', 'Firmar en la tablet', 'Toca «Firmar en esta tablet» y dásela a la clienta. Lee, comprueba su identidad con el servicio de firma y firma con el dedo.'],
    ['lock', 'Volver con tu PIN', 'Mientras la tiene la clienta no ve nada del panel. Para volver, escribe tu PIN.'],
    ['calendar-clock', 'Si no quiere firmar', 'La cita no se cancela sola. Podéis cambiarla de día: le avisamos y se le vuelve a pedir la firma.'],
];
